/**
 * The Advanced → Agents create page, redesigned (task 1312; report
 * `project/reports/innovate-app-agents-page-redesign.md`, revision 3, stage 8).
 *
 * The detail page's panels, blank and in edit mode, on one page with no tabs:
 * there is nothing to show on Prompt, Runs or History before the agent exists.
 * The header takes name, description and Active; Model & runtime sits beside
 * Capabilities (whose MCP and knowledge groups say they attach after the
 * agent is created, the service's rule); a Prompt panel offers the prompt
 * `<select>`; the contract panel edits both directions.
 *
 * Create sends `POST /agents` with the body the previous `AgentForm` built
 * (name, description, isActive, modelConfigId, failoverModelConfigId, tools,
 * and the schemas when set — no metadata), assigns the chosen prompt, then lands
 * on the new agent's Overview. A failed prompt assignment does not undo the
 * create; the User is told and can assign it on the Prompt tab.
 *
 * Behind `innovate.fe.agents.redesign`; off, `App.tsx` routes to the previous
 * `pages/NewAgentPage.tsx` unchanged.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Breadcrumbs from '@/components/layout/Breadcrumbs';
import { AgentIdentityHeader } from '@/components/services/detail/agents/AgentIdentityHeader';
import { AgentModelPanel } from '@/components/services/detail/agents/AgentModelPanel';
import { AgentCapabilitiesPanel } from '@/components/services/detail/agents/AgentCapabilitiesPanel';
import { AgentIoContractPanel } from '@/components/services/detail/agents/AgentIoContractPanel';
import { useAssignPrompt, useCreateAgent } from '@/hooks/useAgents';
import { usePrompts } from '@/hooks/usePrompts';
import { useToast } from '@/contexts/ToastContext';
import { refusalReason } from '@/utils/apiRefusal';
import type { Agent, CreateAgentInput } from '@/types';

/** A blank agent the panels can render in edit mode. No id: nothing is saved yet. */
const blankAgent = (): Agent => ({
  id: '',
  name: '',
  description: '',
  input: null,
  output: null,
  tools: [],
  isActive: true,
  metadata: null,
  modelConfigId: null,
  failoverModelConfigId: null,
  createdBy: '',
  updatedBy: null,
  deletedBy: null,
  createdAt: '',
  updatedAt: '',
  deletedAt: null,
});

const primaryButton =
  'inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50';
const secondaryButton =
  'inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700';

export default function NewAgentPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const createAgent = useCreateAgent();
  const assignPrompt = useAssignPrompt();
  const { data: promptsData, isLoading: promptsLoading } = usePrompts(1, 100);
  const prompts = promptsData?.data ?? [];

  const [draft, setDraft] = useState<Agent>(blankAgent);
  const [promptId, setPromptId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleFieldChange = (field: string, value: unknown) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const canCreate = draft.name.trim().length > 0 && !submitting;

  const handleCreate = async () => {
    if (!draft.name.trim()) {
      toast.error('Agent name is required', 'Give the agent a name before creating it.');
      return;
    }
    // The body the previous AgentForm sent.
    const body: Record<string, unknown> = {
      name: draft.name.trim(),
      description: draft.description || undefined,
      isActive: draft.isActive,
      modelConfigId: draft.modelConfigId || null,
      failoverModelConfigId: draft.failoverModelConfigId || null,
      tools: draft.tools,
    };
    if (draft.input) body.input = draft.input;
    if (draft.output) body.output = draft.output;

    setSubmitting(true);
    try {
      // Cast: a cleared Model config is sent as `null`, which the input type omits.
      const created = await createAgent.mutateAsync(body as unknown as CreateAgentInput);
      if (promptId && created?.id) {
        try {
          await assignPrompt.mutateAsync({ agentId: created.id, promptId });
        } catch (assignError) {
          toast.warning(
            'Agent created, prompt not assigned',
            `${refusalReason(assignError)} Assign it on the Prompt tab.`
          );
        }
      }
      toast.success('Agent created');
      navigate(created?.id ? `/agents/${created.id}` : '/agents');
    } catch (createError) {
      toast.error('Failed to create agent', refusalReason(createError));
    } finally {
      setSubmitting(false);
    }
  };

  const actions = (
    <>
      <button
        type="button"
        onClick={handleCreate}
        disabled={!canCreate}
        data-testid="agent-create"
        className={primaryButton}
      >
        {submitting ? 'Creating…' : 'Create agent'}
      </button>
      <button
        type="button"
        onClick={() => navigate('/agents')}
        disabled={submitting}
        data-testid="agent-create-cancel"
        className={secondaryButton}
      >
        Cancel
      </button>
    </>
  );

  return (
    <div data-testid="new-agent-page">
      <Breadcrumbs items={[{ name: 'Agents', href: '/agents' }, { name: 'New agent' }]} />

      <div className="flex flex-col gap-4">
        <AgentIdentityHeader
          agent={draft}
          showMetadata
          actions={actions}
          editing
          onFieldChange={handleFieldChange}
        />

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <AgentModelPanel
            agent={draft}
            nodeLlmConfigId={undefined}
            editing
            onFieldChange={handleFieldChange}
          />
          <AgentCapabilitiesPanel agent={draft} editing onFieldChange={handleFieldChange} />
        </div>

        <section
          data-testid="new-agent-prompt-panel"
          className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800"
        >
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Prompt</h3>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            Optional — the agent runs on its description alone without one
          </p>
          <label htmlFor="new-agent-prompt" className="mt-4 block text-xs font-medium text-gray-500 dark:text-gray-400">
            Prompt
          </label>
          <select
            id="new-agent-prompt"
            data-testid="new-agent-prompt"
            value={promptId}
            onChange={(event) => setPromptId(event.target.value)}
            disabled={promptsLoading}
            className="mt-1 block w-full max-w-md rounded-md border-gray-300 bg-white text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
          >
            <option value="">No prompt (assign later)</option>
            {prompts.map((prompt) => (
              <option key={prompt.id} value={prompt.id}>
                {prompt.name}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Assigned right after the agent is created.</p>
        </section>

        <AgentIoContractPanel agent={draft} editing onFieldChange={handleFieldChange} />
      </div>
    </div>
  );
}
